import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError } from 'rxjs';
import { ConfigService } from './config.service';
import {
  AccountsSelectResponse,
  BFeeGroupOption,
  FeeManagementRequest,
  FeeManagementResponse,
  FincoreFeeAction,
  FincoreFeeProduct,
  ReferenceDateRequest,
  RowsAffectedResponse,
  RunBatchBfeeGroupRequest,
  RunBatchResponse,
} from '../model/fincore';
import {
  createFincoreHeaders,
  handleFincoreHttpError,
  resolveFincoreBaseUrl,
} from '../util/fincore-http';

@Injectable({
  providedIn: 'root',
})
export class FincoreMonthlyFeeService {
  private URL: string;
  private prefix: string;

  constructor(
    @Inject(ConfigService) private config: any,
    private httpClient: HttpClient
  ) {
    this.URL = resolveFincoreBaseUrl(this.config);
    this.prefix = `${this.URL}/monthly-fees`;
  }

  listBfeeGroups(): Observable<BFeeGroupOption[]> {
    const headers = createFincoreHeaders();
    return this.httpClient
      .get<BFeeGroupOption[]>(`${this.prefix}/bfee-groups`, { headers })
      .pipe(catchError(handleFincoreHttpError));
  }

  runMonthlyFeeBatch(referenceDate: string): Observable<RunBatchResponse> {
    const headers = createFincoreHeaders();
    const body: ReferenceDateRequest = { referenceDate };
    return this.httpClient
      .post<RunBatchResponse>(`${this.prefix}/run-batch`, body, { headers })
      .pipe(catchError(handleFincoreHttpError));
  }

  // See C002 - 2026-08-21 - HE
  // runBatchForBfeeGroup(
  //   bfeeGroupId: number,
  //   referenceDate: string
  // ): Observable<RunBatchResponse> {
  //   const headers = createFincoreHeaders();
  //   const body: RunBatchBfeeGroupRequest = { bfeeGroupId, referenceDate };
  //   return this.httpClient
  //     .post<RunBatchResponse>(`${this.prefix}/run-batch-bfee-group`, body, {
  //       headers,
  //     })
  //     .pipe(catchError(handleFincoreHttpError));
  // }
  runBatchForBfeeGroup(
    request: RunBatchBfeeGroupRequest
  ): Observable<RunBatchResponse>;
  runBatchForBfeeGroup(
    bfeeGroupId: number,
    referenceDate: string
  ): Observable<RunBatchResponse>;
  runBatchForBfeeGroup(
    bfeeGroupIdOrRequest: number | RunBatchBfeeGroupRequest,
    referenceDate?: string
  ): Observable<RunBatchResponse> {
    const headers = createFincoreHeaders();
    const body: RunBatchBfeeGroupRequest =
      typeof bfeeGroupIdOrRequest === 'object'
        ? bfeeGroupIdOrRequest
        : {
            bfeeGroupId: bfeeGroupIdOrRequest,
            referenceDate: referenceDate as string,
          };
    return this.httpClient
      .post<RunBatchResponse>(`${this.prefix}/run-batch-bfee-group`, body, {
        headers,
      })
      .pipe(catchError(handleFincoreHttpError));
  }

  manageAccounts(
    product: FincoreFeeProduct,
    action: FincoreFeeAction,
    referenceDate: string
  ): Observable<FeeManagementResponse> {
    const headers = createFincoreHeaders();
    const body: FeeManagementRequest = { product, action, referenceDate };
    return this.httpClient
      .post<FeeManagementResponse>(`${this.prefix}/accounts`, body, { headers })
      .pipe(catchError(handleFincoreHttpError));
  }

  selectWizzitAccounts(
    referenceDate: string
  ): Observable<AccountsSelectResponse> {
    const headers = createFincoreHeaders();
    const body: ReferenceDateRequest = { referenceDate };
    return this.httpClient
      .post<AccountsSelectResponse>(
        `${this.prefix}/wizzit-accounts/select`,
        body,
        { headers }
      )
      .pipe(catchError(handleFincoreHttpError));
  }

  cancelWizzitBatch(referenceDate: string): Observable<RowsAffectedResponse> {
    const headers = createFincoreHeaders();
    const body: ReferenceDateRequest = { referenceDate };
    return this.httpClient
      .post<RowsAffectedResponse>(`${this.prefix}/cancel-batch`, body, {
        headers,
      })
      .pipe(catchError(handleFincoreHttpError));
  }

  selectSureCardAccounts(
    referenceDate: string
  ): Observable<AccountsSelectResponse> {
    const headers = createFincoreHeaders();
    const body: ReferenceDateRequest = { referenceDate };
    return this.httpClient
      .post<AccountsSelectResponse>(
        `${this.prefix}/surecard-accounts/select`,
        body,
        { headers }
      )
      .pipe(catchError(handleFincoreHttpError));
  }

  cancelSureCardBatch(referenceDate: string): Observable<RowsAffectedResponse> {
    const headers = createFincoreHeaders();
    const body: ReferenceDateRequest = { referenceDate };
    return this.httpClient
      .post<RowsAffectedResponse>(
        `${this.prefix}/cancel-batch-surecard`,
        body,
        { headers }
      )
      .pipe(catchError(handleFincoreHttpError));
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Added FincoreMonthlyFeeService with HttpClient methods for FinCore monthly-fee APIs. Previous behavior: No portaladmin client existed; FinCore UI called HttpClient from App component. New behavior: Service resolves {FINCORE host|REST_SERVER_HOST}/api/fincore/monthly-fees and exposes listBfeeGroups GET /bfee-groups, runMonthlyFeeBatch POST /run-batch, runBatchForBfeeGroup POST /run-batch-bfee-group, manageAccounts POST /accounts (WIZZIT|SURECARD, SELECT|UPDATE), plus dedicated Wizzit/SureCard select and cancel-batch paths. Reason: Replicate FinCore REST API V1.000 in portaladmin without UI. Context: Integration to FinCore; dates are yyyyMMdd. Impact: providedIn root; requires FINCORE in REST_SERVER_url; DoNotIntercept headers match RtcService; no components wired. - 2026-08-20 - HE
// C002: Promoted POST /monthly-fees/run-batch-bfee-group to the official FinCore REST API V1.000 resource "Fee Management - Selected BFeeGroup". Previous behavior: runBatchForBfeeGroup(bfeeGroupId: number, referenceDate: string) only accepted two arguments and was treated as a FinCore-UI extra. New behavior: Method overloads accept the spec payload { bfeeGroupId, referenceDate } or the two arguments; still POSTs to /run-batch-bfee-group; response typed as RunBatchResponse (errors/status/summary plus rowsAffected and calendarMonth). Reason: Spec addition 2026-08-21 lists this as a first-class monthly-fee resource used to generate fees for one BFeeGroup selected from a dropdown. Context: Integration to Fincore REST API V1.000. Impact: Existing two-arg calls still compile; callers can pass the JSON body object; listBfeeGroups() remains the dropdown data source (GET /bfee-groups). - 2026-08-21 - HE
